import { safeCall } from '@/app/utils/safeCall';

interface CopyToClipboardOptions {
  onSuccess?: (text: string) => void;
  onError?: (error: Error) => void;
}

const fallbackCopy = (
  text: string,
  onSuccess?: (text: string) => void,
  onError?: (error: Error) => void,
  silent?: boolean
) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  textArea.setAttribute('readonly', '');

  document.body.appendChild(textArea);
  textArea.select();

  let success = false;

  try {
    // Ignore deprecation warning for older browser support
    success = document.execCommand('copy');
  } catch {
    success = false;
  }

  document.body.removeChild(textArea);

  if (success) {
    return safeCall(onSuccess, text);
  }

  if (!silent) {
    safeCall(onError, new Error('Failed to copy text'));
  }
};

export const copyToClipboard = (
  text: string,
  options: CopyToClipboardOptions = {}
) => {
  const { onSuccess, onError } = options;
  if (navigator.clipboard && window.isSecureContext) {
    let fallbackTriggered = false;

    const timeoutId = setTimeout(() => {
      fallbackTriggered = true;
      fallbackCopy(text, onSuccess, onError);
    }, 100);

    if (typeof navigator.clipboard?.writeText !== 'function') {
      return fallbackCopy(text, onSuccess, onError);
    }

    const promise = navigator.clipboard.writeText(text);
    if (!promise) {
      clearTimeout(timeoutId);
      return fallbackCopy(text, onSuccess, onError);
    }

    promise
      .then(() => {
        clearTimeout(timeoutId);
        if (!fallbackTriggered) {
          safeCall(onSuccess, text);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        // Silently ignore if user cancelled the permission prompt
        if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
          return;
        }

        if (!fallbackTriggered) {
          fallbackCopy(text, onSuccess, onError);
        }
      });
  } else {
    fallbackCopy(text, onSuccess, onError);
  }
};
